/**
 * Firestore real-time listeners for generation runs.
 *
 * Provides onSnapshot listeners that stream updates in real-time
 * instead of polling the REST API.
 */

import {
  collection,
  query,
  orderBy,
  limit as firestoreLimit,
  onSnapshot,
  doc,
  type Unsubscribe,
  type QuerySnapshot,
  type DocumentSnapshot,
} from 'firebase/firestore';
import { getFirestoreDb, getOrganizationId, initAuth } from './firebase';
import type {
  AdPackJob,
  AdPackStatusResponse,
  InsightLogEntry,
  GardenRun,
  CreativeReviewPipelineState,
} from './api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GenerationRunDoc {
  run_id: string;
  client_uid: string;
  status: string;
  run_type: string;
  source_pipeline?: string;
  source_job_id?: string;
  started_at?: string;
  completed_at?: string;
  created_at?: string;
  updated_at?: string;
  archived?: boolean;
  archived_at?: string;
  progress?: {
    current_stage?: string;
    stage_index?: number;
    total_stages?: number;
    tasks_completed?: number;
    tasks_total?: number;
    percent_complete?: number;
    elapsed_seconds?: number;
  };
  execution_log?: {
    current_step?: string;
    steps_completed?: number;
    total_steps?: number;
    started_at?: string;
    completed_at?: string;
    error?: string;
    steps?: Array<{
      step_name: string;
      status: string;
      started_at?: string;
      completed_at?: string;
      items_total?: number;
      items_completed?: number;
      items_failed?: number;
      message?: string;
      error?: string;
    }>;
    insight_log?: InsightLogEntry[];
  };
  metadata?: Record<string, unknown>;
  errors?: Array<{ step: string; error: string; at: string }>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Map a Firestore generation_run document to the AdPackJob format
 * used by the UI (compatible with the REST API response).
 */
function mapRunToJob(run: GenerationRunDoc): AdPackJob {
  const progress = run.progress || {};
  const execLog = run.execution_log || {};

  // Count ads from execution log steps
  let adsGenerated = 0;
  let adsReviewed = 0;
  if (execLog.steps) {
    const genStep = execLog.steps.find((s) => s.step_name === 'generate_images');
    const reviewStep = execLog.steps.find((s) => s.step_name === 'review_ads');
    adsGenerated = genStep?.items_completed || 0;
    adsReviewed = reviewStep?.items_completed || 0;
  }

  return {
    job_id: run.source_job_id || run.run_id,
    client_uid: run.client_uid,
    status: mapStatus(run.status),
    pipeline_phase: progress.current_stage || execLog.current_step,
    percent_complete: progress.percent_complete || 0,
    personas_count: 0, // Not available in run doc directly
    ads_generated: adsGenerated,
    ads_reviewed: adsReviewed,
    created_at: run.created_at || run.started_at,
    completed_at: run.completed_at,
    error: execLog.error || (run.errors?.[0]?.error),
    archived: run.archived,
    archived_at: run.archived_at,
    run_id: run.run_id, // Include run_id for archive operations
  };
}

/**
 * Map a Firestore generation_run document to the detailed AdPackStatusResponse.
 */
function mapRunToStatus(run: GenerationRunDoc): AdPackStatusResponse {
  const progress = run.progress || {};
  const execLog = run.execution_log || {};

  // Build phases from execution log
  const phasesCompleted: string[] = [];
  const phasesRemaining: string[] = [];

  if (execLog.steps) {
    for (const step of execLog.steps) {
      if (step.status === 'completed') {
        phasesCompleted.push(step.step_name);
      } else if (step.status === 'pending') {
        phasesRemaining.push(step.step_name);
      }
      // 'running' and 'cancelled' steps are left as pipeline_phase (current)
    }
  }

  // Count ads from execution log steps
  let adsGenerated = 0;
  let adsReviewed = 0;
  let personasCount = 0;
  if (execLog.steps) {
    const personaStep = execLog.steps.find((s) => s.step_name === 'fetch_personas');
    const genStep = execLog.steps.find((s) => s.step_name === 'generate_images');
    const reviewStep = execLog.steps.find((s) => s.step_name === 'review_ads');
    personasCount = personaStep?.items_completed || 0;
    adsGenerated = genStep?.items_completed || 0;
    adsReviewed = reviewStep?.items_completed || 0;
  }

  return {
    job_id: run.source_job_id || run.run_id,
    run_id: run.run_id, // Firestore document ID — needed for archive operations
    client_uid: run.client_uid,
    status: mapStatus(run.status),
    pipeline_phase: progress.current_stage || execLog.current_step,
    percent_complete: progress.percent_complete || 0,
    phases_completed: phasesCompleted,
    phases_remaining: phasesRemaining,
    personas_count: personasCount,
    ads_generated: adsGenerated,
    ads_reviewed: adsReviewed,
    created_at: run.created_at || run.started_at,
    elapsed_seconds: progress.elapsed_seconds,
    error: execLog.error || (run.errors?.[0]?.error),
    // Pass through full execution_log so the detail view can render insight_log
    execution_log: run.execution_log
      ? {
          steps: (run.execution_log.steps || []).map((s) => ({
            step_name: s.step_name,
            status: s.status,
            started_at: s.started_at,
            completed_at: s.completed_at,
            items_total: s.items_total || 0,
            items_completed: s.items_completed || 0,
            items_failed: s.items_failed || 0,
            message: s.message,
            error: s.error,
          })),
          current_step: run.execution_log.current_step,
          total_steps: run.execution_log.total_steps || 0,
          steps_completed: run.execution_log.steps_completed || 0,
          started_at: run.execution_log.started_at,
          completed_at: run.execution_log.completed_at,
          error: run.execution_log.error,
          insight_log: run.execution_log.insight_log || [],
        }
      : undefined,
  };
}

/**
 * Map backend status to UI-compatible status string.
 */
function mapStatus(status: string): string {
  // Map run status to ad pack status
  const statusMap: Record<string, string> = {
    running: 'running',
    pending: 'pending',
    queued: 'queued',
    completed: 'completed',
    failed: 'failed',
    cancelled: 'cancelled',
  };
  return statusMap[status] || status;
}

// ---------------------------------------------------------------------------
// Listeners
// ---------------------------------------------------------------------------

/**
 * Subscribe to generation runs collection with real-time updates.
 *
 * @param onUpdate - Callback when jobs list updates
 * @param onError - Callback on error
 * @param options - Optional settings { includeArchived: boolean, limitCount: number }
 * @returns Unsubscribe function
 */
export function subscribeToGenerationRuns(
  onUpdate: (jobs: AdPackJob[], total: number) => void,
  onError: (error: Error) => void,
  options: { includeArchived?: boolean; limitCount?: number } = {},
): Unsubscribe {
  const { includeArchived = false, limitCount = 50 } = options;
  let unsubscribed = false;
  let innerUnsubscribe: Unsubscribe | null = null;

  // Initialize auth first, then subscribe
  initAuth()
    .then((user) => {
      if (unsubscribed) return;

      if (!user) {
        console.warn('[Firestore] No authenticated user, subscription may fail');
      }

      const db = getFirestoreDb();
      const orgId = getOrganizationId();

      // Collection path: organizations/{org_uid}/generation_runs
      const runsRef = collection(db, 'organizations', orgId, 'generation_runs');

      // Query: filter by source_pipeline = 'ad_generation', order by created_at DESC
      const runsQuery = query(
        runsRef,
        orderBy('created_at', 'desc'),
        firestoreLimit(limitCount),
      );

      console.log(`[Firestore] Subscribing to generation_runs for org=${orgId}, includeArchived=${includeArchived} (user: ${user?.uid ?? 'none'})`);

      innerUnsubscribe = onSnapshot(
        runsQuery,
        (snapshot: QuerySnapshot) => {
          const jobs: AdPackJob[] = [];

          snapshot.forEach((docSnap) => {
            const data = docSnap.data() as GenerationRunDoc;
            // Only include ad_generation runs
            if (data.source_pipeline === 'ad_generation') {
              // Filter out archived runs unless includeArchived is true
              if (!includeArchived && data.archived) {
                return;
              }
              jobs.push(mapRunToJob(data));
            }
          });

          console.log(`[Firestore] Received ${jobs.length} ad generation jobs`);
          onUpdate(jobs, jobs.length);
        },
        (error) => {
          console.error('[Firestore] Subscription error:', error);
          onError(error);
        },
      );
    })
    .catch((error) => {
      console.error('[Firestore] Auth initialization failed:', error);
      onError(error);
    });

  return () => {
    unsubscribed = true;
    if (innerUnsubscribe) {
      innerUnsubscribe();
    }
  };
}

/**
 * Subscribe to a specific generation run document for real-time detail updates.
 *
 * @param jobId - The ad pack job ID (source_job_id in Firestore)
 * @param onUpdate - Callback when document updates
 * @param onError - Callback on error
 * @returns Unsubscribe function, or null if run not found
 */
export function subscribeToGenerationRun(
  runId: string,
  onUpdate: (status: AdPackStatusResponse | null) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  const db = getFirestoreDb();
  const orgId = getOrganizationId();

  // Document path: organizations/{org_uid}/generation_runs/{run_id}
  const runRef = doc(db, 'organizations', orgId, 'generation_runs', runId);

  console.log(`[Firestore] Subscribing to run ${runId}`);

  const unsubscribe = onSnapshot(
    runRef,
    (docSnap: DocumentSnapshot) => {
      if (!docSnap.exists()) {
        console.log(`[Firestore] Run ${runId} not found`);
        onUpdate(null);
        return;
      }

      const data = docSnap.data() as GenerationRunDoc;
      const status = mapRunToStatus(data);
      console.log(`[Firestore] Run ${runId} updated: ${status.status} ${status.percent_complete}%`);
      onUpdate(status);
    },
    (error) => {
      console.error(`[Firestore] Run ${runId} subscription error:`, error);
      onError(error);
    },
  );

  return unsubscribe;
}

/**
 * Subscribe to a specific generation run document and return full GenerationRun format
 * for the Generation Workflows detail view (not AdPackStatusResponse).
 *
 * Initializes Firebase Auth before subscribing to ensure proper permissions.
 *
 * @param runId - The generation run ID (document ID in Firestore)
 * @param onUpdate - Callback when document updates
 * @param onError - Callback on error
 * @returns Unsubscribe function
 */
export function subscribeToGenerationRunDetail(
  runId: string,
  onUpdate: (run: GenerationRunDoc | null) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  let unsubscribed = false;
  let innerUnsubscribe: Unsubscribe | null = null;

  // Initialize auth first, then subscribe
  initAuth()
    .then((user) => {
      if (unsubscribed) return; // Component unmounted before auth completed

      if (!user) {
        console.warn('[Firestore] No authenticated user, subscription may fail');
      }

      const db = getFirestoreDb();
      const orgId = getOrganizationId();

      // Document path: organizations/{org_uid}/generation_runs/{run_id}
      const runRef = doc(db, 'organizations', orgId, 'generation_runs', runId);

      console.log(`[Firestore] Subscribing to run detail ${runId} (user: ${user?.uid ?? 'none'})`);

      innerUnsubscribe = onSnapshot(
        runRef,
        (docSnap: DocumentSnapshot) => {
          if (!docSnap.exists()) {
            console.log(`[Firestore] Run ${runId} not found`);
            onUpdate(null);
            return;
          }

          const data = docSnap.data() as GenerationRunDoc;
          console.log(`[Firestore] Run detail ${runId} updated: ${data.status} ${data.progress?.percent_complete ?? 0}%`);
          onUpdate(data);
        },
        (error) => {
          console.error(`[Firestore] Run detail ${runId} subscription error:`, error);
          onError(error);
        },
      );
    })
    .catch((error) => {
      console.error('[Firestore] Auth initialization failed:', error);
      onError(error);
    });

  // Return an unsubscribe function that handles async setup
  return () => {
    unsubscribed = true;
    if (innerUnsubscribe) {
      innerUnsubscribe();
    }
  };
}

// ---------------------------------------------------------------------------
// Garden Run listeners (Creative Review)
// ---------------------------------------------------------------------------

/**
 * Subscribe to a garden run document in real-time.
 *
 * Path: organizations/{orgId}/garden_runs/{runId}
 *
 * Mirrors subscribeToGenerationRunDetail for the generation_runs collection.
 * Falls back gracefully when authentication is not ready.
 */
export function subscribeToGardenRun(
  runId: string,
  onUpdate: (run: GardenRun | null) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  let innerUnsubscribe: Unsubscribe | null = null;
  let unsubscribed = false;

  initAuth()
    .then((user) => {
      if (unsubscribed) return;
      if (!user) {
        console.warn('[Firestore] subscribeToGardenRun: no authenticated user');
      }
      const db = getFirestoreDb();
      const orgId = getOrganizationId();
      const runRef = doc(db, 'organizations', orgId, 'garden_runs', runId);

      console.log(`[Firestore] Subscribing to garden run ${runId}`);

      innerUnsubscribe = onSnapshot(
        runRef,
        (docSnap: DocumentSnapshot) => {
          if (!docSnap.exists()) {
            onUpdate(null);
            return;
          }
          onUpdate(docSnap.data() as GardenRun);
        },
        (error) => {
          console.error(`[Firestore] garden run ${runId} error:`, error);
          onError(error);
        },
      );
    })
    .catch((error) => {
      console.error('[Firestore] subscribeToGardenRun auth error:', error);
      onError(error);
    });

  return () => {
    unsubscribed = true;
    if (innerUnsubscribe) innerUnsubscribe();
  };
}

/**
 * Subscribe to the pipelines subcollection for a garden run.
 *
 * Path: organizations/{orgId}/garden_runs/{runId}/pipelines
 *
 * Each document is a CreativeReviewPipelineState for one (creative, persona) pair.
 * The collection grows in real-time as the backfill processes pairs.
 */
export function subscribeToGardenRunPipelines(
  runId: string,
  onUpdate: (pipelines: CreativeReviewPipelineState[]) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  let innerUnsubscribe: Unsubscribe | null = null;
  let unsubscribed = false;

  initAuth()
    .then((user) => {
      if (unsubscribed) return;
      if (!user) {
        console.warn('[Firestore] subscribeToGardenRunPipelines: no authenticated user');
      }
      const db = getFirestoreDb();
      const orgId = getOrganizationId();
      const pipelinesRef = collection(
        db,
        'organizations',
        orgId,
        'garden_runs',
        runId,
        'pipelines',
      );

      console.log(`[Firestore] Subscribing to pipeline docs for run ${runId}`);

      innerUnsubscribe = onSnapshot(
        pipelinesRef,
        (snapshot: QuerySnapshot) => {
          const pipelines = snapshot.docs.map(
            (d) => d.data() as CreativeReviewPipelineState,
          );
          onUpdate(pipelines);
        },
        (error) => {
          console.error(`[Firestore] pipelines ${runId} error:`, error);
          onError(error);
        },
      );
    })
    .catch((error) => {
      console.error('[Firestore] subscribeToGardenRunPipelines auth error:', error);
      onError(error);
    });

  return () => {
    unsubscribed = true;
    if (innerUnsubscribe) innerUnsubscribe();
  };
}

/**
 * Subscribe to the garden_runs collection for a client in real-time.
 *
 * Path: organizations/{orgId}/garden_runs
 * Filtered client-side by client_uid (Firestore doesn't support inequality
 * filters without a composite index, so we fetch recent and filter).
 *
 * Mirrors subscribeToGenerationRuns for the garden_runs collection.
 */
export function subscribeToGardenRuns(
  clientUid: string,
  onUpdate: (runs: GardenRun[]) => void,
  onError: (error: Error) => void,
  options: { limitCount?: number } = {},
): Unsubscribe {
  const { limitCount = 200 } = options;
  let unsubscribed = false;
  let innerUnsubscribe: Unsubscribe | null = null;

  initAuth()
    .then((user) => {
      if (unsubscribed) return;
      if (!user) {
        console.warn('[Firestore] subscribeToGardenRuns: no authenticated user');
      }
      const db = getFirestoreDb();
      const orgId = getOrganizationId();
      const runsRef = collection(db, 'organizations', orgId, 'garden_runs');
      const runsQuery = query(
        runsRef,
        orderBy('started_at', 'desc'),
        firestoreLimit(limitCount),
      );

      console.log(`[Firestore] Subscribing to garden_runs for client=${clientUid}`);

      innerUnsubscribe = onSnapshot(
        runsQuery,
        (snapshot: QuerySnapshot) => {
          const runs: GardenRun[] = snapshot.docs
            .map((d) => d.data() as GardenRun)
            .filter((r) => r.client_uid === clientUid);
          onUpdate(runs);
        },
        (error) => {
          console.error('[Firestore] subscribeToGardenRuns error:', error);
          onError(error);
        },
      );
    })
    .catch((error) => {
      console.error('[Firestore] subscribeToGardenRuns auth error:', error);
      onError(error);
    });

  return () => {
    unsubscribed = true;
    if (innerUnsubscribe) innerUnsubscribe();
  };
}

/**
 * Find the Firestore run_id for an ad pack job_id.
 *
 * The ad pack job_id (e.g., "adpack_abc123") is stored as source_job_id
 * in the generation_runs collection. We need to find the corresponding run_id.
 *
 * This is a one-time query, not a subscription.
 */
export async function findRunIdByJobId(jobId: string): Promise<string | null> {
  const db = getFirestoreDb();
  const orgId = getOrganizationId();

  // Import query helpers
  const { where, getDocs } = await import('firebase/firestore');

  const runsRef = collection(db, 'organizations', orgId, 'generation_runs');
  const q = query(runsRef, where('source_job_id', '==', jobId), firestoreLimit(1));

  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }

  return snapshot.docs[0].id;
}
